import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import useLanguageStore from '@stores/languageStore';
import useCartStore, { SERVICE_TYPES } from '@stores/cartStore';
import obituaryIcon from '@static/obituary-icon.png';
import tombstoneIcon from '@static/tombstone-icon.png';
import refurbishIcon from '@static/refurbish-icon.png';
import rabiIcon from '@static/rabi-icon.png';
import SEOHead from '../../components/SEOHead';

const CartPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const isRtl = language === 'he';
  const navigate = useNavigate();

  const { items, removeItem, updateItemQuantity, clearCart } = useCartStore();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const getServiceName = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return t('home.services.obituary.title');
      case SERVICE_TYPES.TOMBSTONE:
        return t('home.services.tombstone.title');
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return t('home.services.scheduleRabi.title');
      case SERVICE_TYPES.REFURBISH:
        return t('home.services.refurbish.title');
      default:
        return serviceType;
    }
  };

  const getServiceRoute = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return '/obituary';
      case SERVICE_TYPES.TOMBSTONE:
        return '/tombstone';
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return '/schedule-rabbi';
      default:
        return '/services';
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return obituaryIcon;
      case SERVICE_TYPES.TOMBSTONE:
        return tombstoneIcon;
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return rabiIcon;
      case SERVICE_TYPES.REFURBISH:
        return refurbishIcon;
      default:
        return null;
    }
  };

  const getColorLabel = (colorValue) => {
    if (!colorValue) return '';
    if (colorValue === 'gray') return t('tombstone.gray');
    if (colorValue === '#323131' || colorValue === 'black') return t('tombstone.black');
    if (colorValue === 'natural') return t('tombstone.natural');
    return colorValue;
  };

  const handleRemoveClick = (itemId) => {
    setItemToRemove(itemId);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = () => {
    if (itemToRemove) {
      removeItem(itemToRemove);
      setItemToRemove(null);
    }
    setRemoveDialogOpen(false);
  };

  const handleCancelRemove = () => {
    setItemToRemove(null);
    setRemoveDialogOpen(false);
  };

  const handleQuantityDecrease = (itemId, currentQuantity) => {
    if (currentQuantity <= 1) {
      // Quantity will become 0, show confirmation
      setItemToRemove(itemId);
      setRemoveDialogOpen(true);
    } else {
      updateItemQuantity(itemId, currentQuantity - 1);
    }
  };

  const handleClearCartClick = () => {
    setClearDialogOpen(true);
  };

  const handleConfirmClearCart = () => {
    clearCart();
    setClearDialogOpen(false);
  };

  const handleCancelClearCart = () => {
    setClearDialogOpen(false);
  };

  const formatItemData = (item) => {
    if (item.serviceType === SERVICE_TYPES.OBITUARY) {
      return item.data.name ? `${t('cart.item.deceasedName')}: ${item.data.name}` : '';
    } else if (item.serviceType === SERVICE_TYPES.SCHEDULE_RABBI) {
      const parts = [];
      if (item.data.firstName) parts.push(`${t('cart.item.firstName')}: ${item.data.firstName}`);
      if (item.data.date) parts.push(`${t('scheduleRabi.form.date')}: ${item.data.date}`);
      if (item.data.time) parts.push(`${t('scheduleRabi.form.time')}: ${item.data.time}`);
      return parts.join(', ');
    } else if (item.serviceType === SERVICE_TYPES.TOMBSTONE) {
      const colors = [];
      if (item.data.pillowColor) {
        colors.push(`${t('tombstone.pillow')}: ${getColorLabel(item.data.pillowColor)}`);
      }
      if (item.data.mainStoneColor) {
        colors.push(`${t('tombstone.mainStone')}: ${getColorLabel(item.data.mainStoneColor)}`);
      }
      if (item.data.baseColor) {
        colors.push(`${t('tombstone.base')}: ${getColorLabel(item.data.baseColor)}`);
      }
      return colors.join(' • ');
    }
    return '';
  };

  if (items.length === 0) {
    return (
      <div
        className={`min-h-[calc(100vh-64px)] bg-stone-50 py-8 sm:py-16 max-w-[100vw] overflow-x-hidden ${
          isRtl ? 'rtl' : 'ltr'
        }`}
      >
        <SEOHead title={t('cart.title')} />
        <Container maxWidth="lg" className="px-4 sm:px-8">
          <Paper className="p-12 text-center bg-white shadow-lg" elevation={3}>
            <Typography variant="h4" component="h1" className="mb-4 font-bold text-stone-800">
              {t('cart.empty.title')}
            </Typography>
            <Typography variant="body1" className="mb-8 text-stone-600">
              {t('cart.empty.description')}
            </Typography>
            <Button
              component={Link}
              to="/services"
              variant="contained"
              size="large"
              className="px-8 py-3 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
            >
              {t('cart.empty.browseServices')}
            </Button>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div
      className={`min-h-[calc(100vh-64px)] bg-stone-50 py-8 sm:py-16 max-w-[100vw] overflow-x-hidden ${
        isRtl ? 'rtl' : 'ltr'
      }`}
    >
      <Container maxWidth="lg" className="px-4 sm:px-8">
        <div className="mb-8">
          <Typography variant="h3" component="h1" className="mb-2 font-bold text-stone-800">
            {t('cart.title')}
          </Typography>
          <Typography variant="body1" className="text-stone-600">
            {t('cart.subtitle', { count: items.length })}
          </Typography>
        </div>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" component="h2" className="mb-4 font-bold text-stone-800">
              {t('cart.items.title')}
            </Typography>
            {items.map((item) => (
              <Paper key={item.id} className="p-6 mb-4 bg-white shadow-md" elevation={2}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <Box className="flex justify-center">
                      <img
                        src={getServiceIcon(item.serviceType)}
                        alt={getServiceName(item.serviceType)}
                        className="w-12 h-12 object-contain"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography
                      variant="h6"
                      component="h3"
                      className="mb-3 font-semibold text-stone-800"
                    >
                      {getServiceName(item.serviceType)}
                    </Typography>
                    {item.serviceType === SERVICE_TYPES.TOMBSTONE && (
                      <Box className="mb-3 space-y-1.5">
                        {item.data.pillowColor && (
                          <Typography variant="body2" className="text-stone-600">
                            <span className="font-medium text-stone-700">
                              {t('tombstone.pillow')}:
                            </span>{' '}
                            {getColorLabel(item.data.pillowColor)}
                          </Typography>
                        )}
                        {item.data.mainStoneColor && (
                          <Typography variant="body2" className="text-stone-600">
                            <span className="font-medium text-stone-700">
                              {t('tombstone.mainStone')}:
                            </span>{' '}
                            {getColorLabel(item.data.mainStoneColor)}
                          </Typography>
                        )}
                        {item.data.baseColor && (
                          <Typography variant="body2" className="text-stone-600">
                            <span className="font-medium text-stone-700">
                              {t('tombstone.base')}:
                            </span>{' '}
                            {getColorLabel(item.data.baseColor)}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {item.serviceType !== SERVICE_TYPES.TOMBSTONE && formatItemData(item) && (
                      <Typography variant="body2" className="mb-3 text-stone-600">
                        {formatItemData(item)}
                      </Typography>
                    )}
                    <Button
                      component={Link}
                      to={`${getServiceRoute(item.serviceType)}?edit=${item.id}`}
                      size="small"
                      className="normal-case text-stone-600 hover:text-stone-800"
                    >
                      {t('cart.item.edit')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box className={`flex items-center justify-${isRtl ? 'start' : 'end'} gap-2`}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityDecrease(item.id, item.quantity)}
                        className="text-stone-600 hover:text-stone-800"
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="body1"
                        className="mx-2 font-semibold text-stone-800 min-w-[2rem] text-center"
                      >
                        {item.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                        className="text-stone-600 hover:text-stone-800"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveClick(item.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper className="p-6 bg-white shadow-lg sticky top-24" elevation={3}>
              <Typography variant="h5" component="h3" className="mb-4 font-bold text-stone-800">
                {t('cart.summary.title')}
              </Typography>
              <Box className="mb-4 pb-4 border-b border-stone-200">
                <Typography variant="body1" className="text-stone-600">
                  {t('cart.summary.items', {
                    count: items.reduce((sum, item) => sum + item.quantity, 0),
                  })}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate('/order')}
                className="mb-3 py-3 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
              >
                {t('cart.summary.proceedToOrder')}
              </Button>
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                onClick={handleClearCartClick}
                className="normal-case text-stone-600 border-stone-300 hover:border-stone-400"
              >
                {t('cart.summary.clearCart')}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCancelRemove}
        aria-labelledby="remove-dialog-title"
        aria-describedby="remove-dialog-description"
      >
        <DialogTitle id="remove-dialog-title" className={isRtl ? 'text-right' : 'text-left'}>
          {t('cart.removeConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="remove-dialog-description"
            className={isRtl ? 'text-right' : 'text-left'}
          >
            {t('cart.removeConfirm.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={isRtl ? 'flex-row-reverse' : ''}>
          <Button onClick={handleCancelRemove} className="normal-case text-stone-600">
            {t('cart.removeConfirm.cancel')}
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="secondary"
            variant="contained"
            className="normal-case bg-red-600 hover:bg-red-700"
          >
            {t('cart.removeConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Cart Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleCancelClearCart}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title" className={isRtl ? 'text-right' : 'text-left'}>
          {t('cart.clearConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="clear-dialog-description"
            className={isRtl ? 'text-right' : 'text-left'}
          >
            {t('cart.clearConfirm.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={isRtl ? 'flex-row-reverse' : ''}>
          <Button onClick={handleCancelClearCart} className="normal-case text-stone-600">
            {t('cart.clearConfirm.cancel')}
          </Button>
          <Button
            onClick={handleConfirmClearCart}
            color="secondary"
            variant="contained"
            className="normal-case bg-red-600 hover:bg-red-700"
          >
            {t('cart.clearConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CartPage;
